// components/ExcelUploadStep.jsx
import { useState, useRef, useEffect } from "react";
import { FormGroup, Label, FormFeedback } from "reactstrap";
import {
  RefreshCcw,
  Trash2,
  FileSpreadsheet,
  CheckCircle2,
  Database,
  Key,
  ChevronDown,
} from "lucide-react";
import { showError, showSuccess } from "./AppToast";
import { parseExcelToFields, humanizeHeader } from "../utils/excelParser";
import "../styles/excel-upload.css";

// ── Constants ──────────────────────────────────────────────────────────────
const SUPPORTED_EXCEL_FORMATS = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "application/csv",
];
const MAX_EXCEL_SIZE = 20 * 1024 * 1024;
const LS_FIELDS_KEY = "excel_fields";
const LS_ROWS_KEY = "excel_rows";
const LS_SEARCH_KEY_KEY = "excel_search_key";
const LS_FILE_META_KEY = "excel_file_meta";



const FIELD_TYPES = [
  { value: "text", label: "Input (text)" },
  { value: "textarea", label: "Text Area" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone Number" },
  { value: "amount", label: "Amount" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Radio Group" },
  { value: "checkbox", label: "Checkbox" },
  { value: "address", label: "Address" },
];

function TypeBadge({ type }) {
  return (
    <span className={`eu-type-badge eu-type-badge--${type}`}>
      {FIELD_TYPES.find((t) => t.value === type)?.label ?? type}
    </span>
  );
}

// ── Backend upload ─────────────────────────────────────────────────────────
async function uploadRowsToBackend(rows, searchKey) {

  return new Promise((resolve) =>
    setTimeout(() => resolve({ success: true, inserted: rows.length }), 600),
  );
}

// Ensures every field carries a stable originalId alongside its (now
// always-natural) id. Kept as a small pass-through helper so the rest of
// the component — and any downstream consumer that still reads
// originalId — doesn't need to change shape.
function withOriginalIds(fields) {
  return fields.map((f) => {
    const naturalId = f.originalId || f.id;
    return { ...f, id: naturalId, originalId: naturalId };
  });
}

// ── localStorage helpers ───────────────────────────────────────────────────
function restoreFields(fields) {
  return fields.map((f) => ({
    ...f,
    label: humanizeHeader(f.rawHeader || f.label || f.id),
    // Ensure searchKey is boolean (in case it's missing from old data)
    searchKey: !!f.searchKey,
    // Fall back to the current id for older stored data that predates
    // originalId tracking.
    originalId: f.originalId || f.id,
  }));
}

function readFromLocalStorage() {
  try {
    const rawFields = localStorage.getItem(LS_FIELDS_KEY);
    const rawMeta = localStorage.getItem(LS_FILE_META_KEY);
    const rawKey = localStorage.getItem(LS_SEARCH_KEY_KEY);
    if (!rawFields || !rawMeta) return null;
    return {
      fields: restoreFields(JSON.parse(rawFields)),
      meta: JSON.parse(rawMeta),
      searchKey: rawKey || "",
    };
  } catch (_) {
    return null;
  }
}

// ── Component ──────────────────────────────────────────────────────────────
export default function ExcelUploadStep({ formik }) {
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileMeta, setFileMeta] = useState(null);
  const [parsedFields, setParsedFields] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [restored_row_values, setRowValues] = useState("");

  const [searchKey, setSearchKey] = useState("");
  const [searchKeyErr, setSearchKeyErr] = useState("");
  const [committed, setCommitted] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);

  const fileRef = useRef(null);
  const hasFile = !!formik.values.excelFile;
  const hasColumns = parsedFields.length > 0;

  // ── Restore from localStorage on mount ────────────────────────────────
  useEffect(() => {
    const stored = readFromLocalStorage();
    if (!stored) return;

    // Defensively re-normalize ids on restore, in case the stored data
    // predates originalId tracking or has drifted.
    const restoredFields = withOriginalIds(stored.fields);
    const restoredKey = stored.searchKey;

    setParsedFields(restoredFields);
    setFileMeta({ name: stored.meta.name, size: stored.meta.size });
    setRowCount(stored.meta.rowCount || 0);
    setSearchKey(restoredKey); // ← FIX 1: was missing, caused blank dropdown on revisit
    setCommitted(!!restoredKey);
    setUploadDone(true);

    formik.setValues({
      ...formik.values,
      excelFile: formik.values.excelFile || "__restored__",
      excelColumns: restoredFields,
      excelSearchKey: restoredKey,
    });
    localStorage.setItem(LS_FIELDS_KEY, JSON.stringify(restoredFields));
  }, []);

  // ── Full Reset Helper ─────────────────────────────────────────────────────
  const resetAllStatesAndFormik = () => {
    setCommitted(false);
    setUploadDone(false);
    setParsedFields([]);
    setRowCount(0);
    setSearchKey("");
    setSearchKeyErr("");
    setFileMeta(null);

    // Clear Formik completely
    formik.setFieldValue("excelFile", null);
    formik.setFieldValue("excelColumns", []);
    formik.setFieldValue("excelRows", []);
    formik.setFieldValue("excelSearchKey", "");

    // Clear localStorage
    localStorage.removeItem(LS_FIELDS_KEY);
    localStorage.removeItem(LS_ROWS_KEY);
    localStorage.removeItem(LS_SEARCH_KEY_KEY);
    localStorage.removeItem(LS_FILE_META_KEY);
  };

  // ── File handler ──────────────────────────────────────────────────────────
  // ── File handler ──────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    const validExt = ["xlsx", "xls", "csv"].includes(ext);

    if (!SUPPORTED_EXCEL_FORMATS.includes(file.type) && !validExt) {
      showError("Invalid file type. Please upload .xlsx, .xls, or .csv");
      return;
    }
    if (file.size > MAX_EXCEL_SIZE) {
      showError("File size must be less than 20 MB");
      return;
    }

    // Step 1: Full clean reset BEFORE processing new file (fixes replace issue)
    resetAllStatesAndFormik();

    setFileMeta({
      name: file.name,
      size: (file.size / 1024).toFixed(1) + " KB",
    });

    setLoading(true);
    let fields = [],
      rows = [];

    try {
      ({ fields, rows } = await parseExcelToFields(file));

      // ── NEW VALIDATION: If only headers (no data rows) ─────────────────────
      if (rows.length === 0) {
        showError(
          "The uploaded file contains only headers. Please add at least one data row.",
        );
        resetAllStatesAndFormik();
        setFileMeta(null);
        return;
      }

      if (rows.length > 2000) {
        showError(
          `Maximum 2000 rows allowed. Your file has ${rows.length} rows.`,
        );
        resetAllStatesAndFormik();
        setFileMeta(null);
        return;
      }

     
      const withNaturalIds = fields.map((f) => ({ ...f, originalId: f.id }));
      const normalizedFields = withOriginalIds(withNaturalIds);

      // Success path
      setParsedFields(normalizedFields);
      setRowCount(rows.length);

      // Atomic update - Formik + localStorage with latest data
      formik.setValues({
        ...formik.values,
        excelFile: file,
        excelColumns: normalizedFields,
        excelRows: rows, // ← Latest rows
        excelSearchKey: "",
      });

      // Save to localStorage (this ensures replace always has latest rows)
      localStorage.setItem(LS_FIELDS_KEY, JSON.stringify(normalizedFields));
      localStorage.setItem(LS_ROWS_KEY, JSON.stringify(rows)); // ← Guaranteed latest
      localStorage.setItem(
        LS_FILE_META_KEY,
        JSON.stringify({
          name: file.name,
          size: (file.size / 1024).toFixed(1) + " KB",
          rowCount: rows.length,
        }),
      );
    } catch (err) {
      showError(err.message || "Failed to read Excel file.");
      resetAllStatesAndFormik();
      setFileMeta(null);
      return;
    } finally {
      setLoading(false);
    }

    // Backend upload
    setUploading(true);
    try {
      await uploadRowsToBackend(rows, "");
      setUploadDone(true);
    } catch (err) {
      console.error("Row upload failed:", err);
      showError(
        "Columns were read but data upload failed. You can still continue.",
      );
    } finally {
      setUploading(false);
    }
  };
  // Full reset helper - used for both Remove and Replace

  // ── Field type override ────────────────────────────────────────────────
  const updateType = (id, newType) => {
    setParsedFields((prev) => {
      const updated = prev.map((f) => {
        if (f.id !== id) return f;

        const options =
          (newType === "select" || newType === "radio") && !f.options?.length
            ? ["Option 1", "Option 2"]
            : f.options || [];

        return {
          ...f,
          type: newType,
          options,
          // Preserve searchKey flag when changing type
          searchKey: f.searchKey ?? false,
        };
      });

      
      const normalized = withOriginalIds(updated);
      formik.setFieldValue("excelColumns", normalized);
      localStorage.setItem(LS_FIELDS_KEY, JSON.stringify(normalized));
      return normalized;
    });

    if (committed) setCommitted(false);
  };

  // ── Search key change ──────────────────────────────────────────────────
  // KEY FIX: update formik.values.excelSearchKey immediately on every
  // change — do NOT clear excelColumns. This keeps both Yup fields valid
  // so the Next button stays enabled as long as columns exist + a key is set.
  const handleSearchKeyChange = (val) => {
    setSearchKey(val);
    setSearchKeyErr("");
    setCommitted(false);

    // Update fields immediately with new searchKey flag
    if (parsedFields.length > 0) {
      const updatedFields = parsedFields.map((field) => ({
        ...field,
        searchKey: field.id === val,
      }));

      setParsedFields(updatedFields);

      // Also update Formik immediately (important for validation)
      formik.setFieldValue("excelColumns", updatedFields);
      localStorage.setItem(LS_FIELDS_KEY, JSON.stringify(updatedFields));
    }

    // Write searchKey to formik
    formik.setFieldValue("excelSearchKey", val);

    // Persist to localStorage
    if (val) {
      localStorage.setItem(LS_SEARCH_KEY_KEY, val);
    } else {
      localStorage.removeItem(LS_SEARCH_KEY_KEY);
    }
  };
  const commitFields = () => {
  if (!parsedFields.length) {
    showError("No fields to save.");
    return;
  }
  if (!searchKey) {
    setSearchKeyErr("Please select a search key before continuing.");
    return;
  }

  const finalFields = parsedFields.map((field) => ({
    ...field,
    searchKey: field.id === searchKey,
  }));

  formik.setFieldValue("excelColumns", finalFields);
  formik.setFieldValue("excelSearchKey", searchKey);

  localStorage.setItem(LS_FIELDS_KEY, JSON.stringify(finalFields));
  localStorage.setItem(LS_SEARCH_KEY_KEY, searchKey);

  setCommitted(true);

  const keyLabel =
    parsedFields.find((f) => f.id === searchKey)?.label || searchKey;
  showSuccess(`${finalFields.length} fields mapped · search key: "${keyLabel}"`);
};

  // ── Remove file ────────────────────────────────────────────────────────
  const removeFile = () => {
    resetAllStatesAndFormik();
  };

  const selectedKeyLabel =
    parsedFields.find((f) => f.id === searchKey)?.label || "";

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="eu-root">
      <FormGroup>
        <Label>   
      In the Excel file you upload, ensure that every value defined under the 
      <strong> Search Key</strong> column does not exceed <strong>100 characters</strong></Label>

        {/* Drop zone */}
        <div
          className={`px-dropzone ${dragOver ? "drag-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files[0]);
          }}
          onClick={() => !hasFile && !loading && fileRef.current.click()}
          style={{ cursor: hasFile ? "default" : "pointer" }}
        >
          <input
            ref={fileRef}
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              handleFile(e.target.files[0]);
              e.target.value = "";
            }}
          />

          {/* LOADING */}
          {loading && (
            <div className="px-drop-empty">
              <div
                className="spinner-border spinner-border-sm text-primary mb-2"
                role="status"
              />
              <div className="px-drop-title" style={{ fontSize: 14 }}>
                Reading &amp; analysing columns…
              </div>
              <div className="px-drop-sub">
                Detecting field types from your data
              </div>
            </div>
          )}

          {/* EMPTY */}
          {!hasFile && !hasColumns && !loading && (
            <div className="px-drop-empty">
              <div style={{ marginBottom: 10, opacity: 0.5 }}>
                <FileSpreadsheet
                  size={38}
                  strokeWidth={1.2}
                  className="text-primary"
                />
              </div>
              <div className="px-drop-title">Drop Excel file here</div>
              <div className="px-drop-sub">
                or click to browse · .xlsx, .xls, .csv
              </div>
            </div>
          )}

          {/* FILE LOADED */}
          {(hasFile || hasColumns) && !loading && fileMeta && (
            <div className="px-avatar-preview">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FileSpreadsheet
                  size={44}
                  strokeWidth={1}
                  className="text-primary"
                />
                <div className="px-file-meta" style={{ textAlign: "center" }}>
                  <strong>{fileMeta.name}</strong>
                  <span>{fileMeta.size}</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    justifyContent: "center",
                  }}
                >
                  {parsedFields.length > 0 && (
                    <span className="eu-pill eu-pill--columns">
                      📋 {parsedFields.length} columns
                    </span>
                  )}
                  {uploading && (
                    <span className="eu-pill eu-pill--uploading">
                      <span
                        className="spinner-border spinner-border-sm"
                        style={{ width: 10, height: 10, borderWidth: 2 }}
                      />
                      Uploading rows…
                    </span>
                  )}
                  {uploadDone && !uploading && (
                    <span className="eu-pill eu-pill--saved">
                      <Database size={11} /> {rowCount} rows saved
                    </span>
                  )}
                  {searchKey && selectedKeyLabel && (
                    <span className="eu-pill eu-pill--key">
                      <Key size={11} /> {selectedKeyLabel}
                    </span>
                  )}
                </div>
              </div>

              <div className="px-avatar-actions modern">
                <button
                  type="button"
                  className="px-icon-btn"
                  data-tooltip="Replace file"
                  onClick={() => fileRef.current.click()}
                >
                  <RefreshCcw size={16} />
                </button>
                <button
                  type="button"
                  className="px-icon-btn danger"
                  data-tooltip="Remove file"
                  onClick={removeFile}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Validation errors */}
        {formik.touched.excelFile && formik.errors.excelFile && (
          <FormFeedback className="d-block">
            {formik.errors.excelFile}
          </FormFeedback>
        )}
        {formik.touched.excelColumns && formik.errors.excelColumns && (
          <FormFeedback className="d-block">
            {formik.errors.excelColumns}
          </FormFeedback>
        )}
        {formik.touched.excelSearchKey && formik.errors.excelSearchKey && (
          <FormFeedback className="d-block">
            {formik.errors.excelSearchKey}
          </FormFeedback>
        )}

        <div className="px-upload-hint">
          Supported: XLSX, XLS, CSV · Max 20 MB · Headers must be in row 1
        </div>

        {/* Post-parse section */}
        {hasColumns && !loading && (
          <div className="eu-section">
            {/* Search Key card */}
            <div
              className={`eu-sk-card ${searchKeyErr ? "eu-sk-card--error" : searchKey ? "eu-sk-card--active" : ""}`}
            >
              {/* Header bar */}
              <div className="eu-sk-header">
                <div className="eu-sk-icon">
                  <Key size={13} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="eu-sk-title">Search Key</div>
                  <div className="eu-sk-subtitle">
                    The field used to look up a record (e.g. Admission Number,
                    Roll No)
                  </div>
                </div>
                {searchKey && selectedKeyLabel && (
                  <div className="eu-sk-pill">
                    <span className="eu-sk-pill__label">
                      {selectedKeyLabel}
                    </span>
                    <code className="eu-sk-pill__code">{searchKey}</code>
                  </div>
                )}
              </div>

              {/* Select */}
              <div className="eu-sk-select-wrap">
                <select
                  value={searchKey}
                  onChange={(e) => handleSearchKeyChange(e.target.value)}
                  className={`eu-sk-select ${searchKey ? "eu-sk-select--active" : ""}`}
                >
                  <option value="" disabled>
                    — Select a column as search key —
                  </option>
                  {parsedFields.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} className="eu-sk-chevron" />
              </div>

              {searchKeyErr && (
                <div className="eu-sk-error">⚠ {searchKeyErr}</div>
              )}
            </div>

            {/* Column table meta */}
            <div className="eu-table-meta">
              <div>
                <span className="eu-table-meta__count">
                  {parsedFields.length} column
                  {parsedFields.length !== 1 ? "s" : ""} detected
                </span>
                <span className="eu-table-meta__hint">
                  — override types if needed
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {uploadDone && (
                  <span className="eu-table-meta__saved">
                    <Database size={12} /> {rowCount} rows saved
                  </span>
                )}
                {committed && (
                  <span className="eu-table-meta__committed">
                    <CheckCircle2 size={14} /> Saved
                  </span>
                )}
              </div>
            </div>

            {/* Column table */}
            <div className="eu-table">
              <div className="eu-table__head">
                <span>Label (shown in form)</span>
                <span>Normalized id</span>
                <span>Field type</span>
              </div>

              {parsedFields.map((field, idx) => {
                const isSearchKey = searchKey === field.id;
                const rowClass = [
                  "eu-table__row",
                  isSearchKey
                    ? "eu-table__row--search-key"
                    : idx % 2 === 0
                      ? "eu-table__row--even"
                      : "eu-table__row--odd",
                ].join(" ");

                return (
                  <div key={field.originalId || field.id} className={rowClass}>
                    {/* Label */}
                    <div>
                      <div className="eu-table__label">{field.label}</div>
                      <div className="eu-table__badges">
                        <TypeBadge type={field.type} />
                        {isSearchKey && (
                          <span className="eu-table__key-tag">
                            <Key size={9} /> key
                          </span>
                        )}
                        {(field.type === "select" || field.type === "radio") &&
                          field.options?.length > 0 && (
                            <span className="eu-table__options-hint">
                              {field.options.slice(0, 3).join(", ")}
                              {field.options.length > 3 ? "…" : ""}
                            </span>
                          )}
                      </div>
                    </div>

                    {/* Normalized id */}
                    <code className="eu-table__code">
                      {field.originalId || field.id}
                    </code>

                    {/* Type override */}
                    <select
                      value={field.type}
                      onChange={(e) => updateType(field.id, e.target.value)}
                      className="eu-table__type-select"
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="eu-footer">
              {uploading && (
                <span className="eu-saving-indicator">
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ width: 12, height: 12, borderWidth: 2 }}
                  />
                  Saving rows…
                </span>
              )}
              <button
                type="button"
                onClick={commitFields}
                disabled={uploading || !searchKey}
                className={`eu-commit-btn ${committed ? "eu-commit-btn--done" : ""}`}
              >
                {committed ? (
                  <>
                    <CheckCircle2 size={14} /> Fields confirmed
                  </>
                ) : (
                  `Confirm these ${parsedFields.length} fields →`
                )}
              </button>
            </div>

            {/* Helper text — shows state clearly to the user */}
            <p className="eu-hint-text">
              {!searchKey
                ? "Select a search key — Next will be enabled once a key is chosen"
                : !committed
                  ? "Search key set — you can proceed. Optionally click Confirm to lock in type changes."
                  : "Fields confirmed and ready"}
            </p>
          </div>
        )}
      </FormGroup>
    </div>
  );
}
